import {
    AutomationModel,
    AutomationsFactory,
    Button,
    Chip,
    DataTable,
    DialogHandler,
    EmptyContent,
    fetchRoute,
    FullBar,
    GenericWrapperContext,
    Header,
    Icon,
    LoadingContainer,
    TDataTableColumnsData,
    TGenericListReturn,
    TOrder,
    TPromisePoolPartialList,
    TPromisePoolRef,
    TPromisePoolReturnData
} from "@cakemail-org/ui-components-v2"
import { TDropMenuItem } from "@cakemail-org/ui-components-v2/dist/esm/components/dropMenu/types"
import { Stack } from "@mui/material"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { showDeleteDialog } from "../../../components/dialogs/deleteDialog"
import { showRenameDialog } from "../../../components/dialogs/rename"
import { listsPromisePool } from "../../../utils/promisesPools"
import { ERoutes } from "../../../utils/url"
import { TAutomationsListing } from "../types"
import { getListingColsData, getListingRows } from "../utils"

export function AutomationsListing({ }: TAutomationsListing) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { t } = useTranslation()

    const [loading, setLoading] = useState(false)
    const [order, setOrder] = useState<TOrder>("asc")
    const [orderBy, setOrderBy] = useState<string>("created_on")
    const [page, setPage] = useState<number>(1)
    const [perPage, setPerPage] = useState<number>(25)
    const [automations, setAutomations] = useState<TGenericListReturn<AutomationModel>>()
    const [partialLists, setPartialLists] = useState<TPromisePoolReturnData<TPromisePoolPartialList>>({})

    const partialListsPromiseRef = useRef<TPromisePoolRef>(undefined)

    useEffect(() => {
        return stopPromisePools
    }, [])

    useEffect(() => {
        fetchAutomations()
    }, [page, perPage, order, orderBy])

    function stopPromisePools() {
        try {
            partialListsPromiseRef.current?.stop()
            partialListsPromiseRef.current = undefined
        } catch (_e) {
            // pass
        }
    }

    function fetchListsWithPromisePool(automationsList: AutomationModel[]) {
        const listIds = [...new Set(automationsList.map(automation => automation.list_id).filter(Boolean))].map(id => ({ id }))

        if (listIds.length > 0) {
            listsPromisePool({ data: listIds, setter: setPartialLists, poolRef: partialListsPromiseRef })
        }
    }

    function fetchAutomations() {
        setLoading(true)
        Promise.all([
            AutomationsFactory.list({
                withCount: true,
                page: page,
                perPage: perPage
            }),
            AutomationsFactory.listWorkflows({
                withCount: true,
                page: page,
                perPage: perPage
            })
        ]).then(([automationsData, workflowsData]) => {
            // Filter out A/B tests (editor_version === "manual")
            const filteredAutomations = automationsData.data.filter(
                (automation: AutomationModel) => automation.editor_version !== "manual"
            )
            const allData = [...filteredAutomations, ...workflowsData.data]

            const combined = {
                data: allData,
                pagination: {
                    count: filteredAutomations.length + (workflowsData.pagination?.count || 0),
                    page: page,
                    per_page: perPage
                }
            }
            setAutomations(combined as any)
            fetchListsWithPromisePool(allData)
        }).catch(() => {
            DialogHandler.show({
                isExpress: true,
                type: "error",
                title: t("general-error-action")
            })
        }).finally(() => {
            setLoading(false)
        })
    }

    function handleCreateAutomation() {
        window.location.href = "#" + fetchRoute({ route: ERoutes.createAutomation })
    }

    function handlePaginationChange(cPage: number, cPerPage: number) {
        setPage(cPage)
        setPerPage(cPerPage)
    }

    function renderStatusChip(status: string) {
        const isActive = status === "active"
        return (
            <Chip
                label={isActive ? t("general-on") : t("general-off")}
                color={isActive ? "secondary" : "cloud"}
                size="small"
            />
        )
    }

    function handleViewAutomation(automation: any) {
        window.location.href = "#" + fetchRoute({ route: ERoutes.automationEdit, params: { automationId: automation.id } })
    }

    function handleDeleteAutomation(automation: AutomationModel) {
        showDeleteDialog({
            name: automation.name || "",
            resourceKey: "general-automation_one"
        })
            .then(() => {
                return automation.delete()
            })
            .then(() => {
                DialogHandler.show({
                    isExpress: true,
                    type: "success",
                    title: t("general-resource-deleted", { resource: t("general-automation_one") })
                })
                fetchAutomations()
            })
            .catch((e) => {
                if (e) {
                    DialogHandler.show({
                        isExpress: true,
                        type: "error",
                        title: t("general-error-action")
                    })
                }
            })
    }

    function handleRenameAutomation(automation: AutomationModel) {
        showRenameDialog({
            initName: automation.name,
            resourceKey: "general-automation_one"
        })
            .then(async ({ value }) => {
                return AutomationsFactory.get({ id: automation.id }).then((data) => {
                    data.name = value
                    return automation.save(data)
                }).catch((error) => {
                    throw error
                })
            })
            .then(() => {
                fetchAutomations()
                DialogHandler.hide()
            })
            .catch((e) => {
                if (e) {
                    DialogHandler.show({
                        isExpress: true,
                        type: "error",
                        title: t("general-error-action")
                    })
                }
            })
    }

    const colsData: TDataTableColumnsData[] = useMemo(getListingColsData(), [])

    function getActionsDropdownOptions(automation: AutomationModel): TDropMenuItem[] {
        return [
            {
                isAction: true,
                text: t("general-rename"),
                onClick: () => { handleRenameAutomation(automation) }
            },
            {
                isAction: true,
                text: t("general-delete"),
                onClick: () => { handleDeleteAutomation(automation) }
            }
        ]
    }

    const rows: any[] = automations && automations.data.map((automation: AutomationModel, index) => {

        const partialList: TPromisePoolPartialList = partialLists?.[automation.list_id] || {} as TPromisePoolPartialList

        return getListingRows({
            index,
            automation,
            partialList,
            handleViewAutomation,
            getActionsDropdownOptions,
            renderStatusChip
        })
    }) || []

    return (
        <Stack gap={8}>
            <FullBar
                className={loading ? "loading" : ""}
                left={<Header
                    heading={t("general-automation_other")}
                    headingVariant="titleL"
                    support={genericWrapper.account?.name}
                    headingTooltip={t("")}
                />}
                right={[
                    <Button
                        key="create-automation"
                        variant="contained"
                        size="medium"
                        color="secondary"
                        onClick={handleCreateAutomation}
                        iconOnly
                        disabled={loading}
                    >
                        <Icon name="Add18" />
                    </Button>
                ]}
            />

            <LoadingContainer loading={!automations}>

                {automations && automations?.data?.length === 0 &&
                    <EmptyContent
                        title={t("automations-listing-empty-title")}
                        desc={t("automations-listing-empty-desc")}
                        iconName="CreateForm44"
                        button={<Button
                            variant="contained"
                            onClick={handleCreateAutomation}
                            size="medium"
                            color="secondary"
                            iconOnly>
                            {`+ ${t('general-add-user')}`}
                        </Button>}
                    />
                }

                {automations && automations.data.length > 0 &&
                    <DataTable
                        initState={{
                            order,
                            orderBy
                        }}
                        totalCount={automations.pagination.count}
                        className={`automationsDataTable ${loading ? "loading" : ""}`}
                        onPaginationChange={handlePaginationChange}
                        rows={rows}
                        columnsData={colsData}
                        stickyLastCol
                        showPagination={true}
                    />
                }
            </LoadingContainer>
        </Stack>
    )
}